import React from 'react';
import { Info, Loader2 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Tooltip } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import type {
  PipeUploadActivityPoint,
  PipeUploadActivityResponse,
} from '../api/pipeStatusOverviewTypes';
import { formatOverviewGroupLabel } from '../api/pipeStatusOverviewTypes';

const CHART_COLORS = {
  firstIngest: '#00273F',
  rescan: '#EB1C37',
} as const;

const CHART_AXIS_TICK = {
  fontSize: 11,
  fill: '#969696',
  fontFamily: 'Montserrat',
};

const CHART_GRID_STROKE = '#EEF0F3';

interface PipeUploadActivityInsightsProps {
  data: PipeUploadActivityResponse | null;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
}

type KpiAccent = 'navy' | 'green' | 'red' | 'blue' | 'amber';

const ACCENT_TOP: Record<KpiAccent, string> = {
  navy: 'bg-blueprint-blue',
  green: 'bg-galvanizing-green',
  red: 'bg-readable-red',
  blue: 'bg-[#2f6fe0]',
  amber: 'bg-thin-yellow',
};

function InsightsInfoTip({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Tooltip
      content={tooltip}
      placement="top"
      contentClassName="!bg-blueprint-blue text-[12px] leading-snug max-w-[260px]"
    >
      <button
        type="button"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-heritage-grey hover:text-blueprint-blue"
        aria-label={`About ${label}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

function KpiCard({
  label,
  tooltip,
  value,
  sub,
  accent,
}: {
  label: string;
  tooltip: string;
  value: React.ReactNode;
  sub: string;
  accent: KpiAccent;
}) {
  return (
    <div className="relative overflow-hidden rounded-[10px] border border-[#E4E9F0] bg-white p-4 shadow-sm">
      <div className={cn('absolute inset-x-0 top-0 h-[3px]', ACCENT_TOP[accent])} />
      <div className="mb-2.5 flex items-start justify-between gap-2">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-heritage-grey">
          {label}
        </div>
        <InsightsInfoTip label={label} tooltip={tooltip} />
      </div>
      <div className="mb-1.5 font-heading text-[26px] font-extrabold leading-none text-blueprint-blue tabular-nums">
        {value}
      </div>
      <div className="text-[11px] text-heritage-grey">{sub}</div>
    </div>
  );
}

function KpiSkeleton() {
  return (
    <div className="rounded-[10px] border border-[#E4E9F0] bg-white p-4 shadow-sm animate-pulse">
      <div className="mb-3 h-3 w-24 rounded bg-[#EEF0F3]" />
      <div className="mb-2 h-7 w-16 rounded bg-[#EEF0F3]" />
      <div className="h-3 w-32 rounded bg-[#EEF0F3]" />
    </div>
  );
}

function ChartCard({
  title,
  description,
  tooltip,
  empty,
  headerRight,
  chartHeightClassName = 'h-[230px]',
  children,
}: {
  title: string;
  description: string;
  tooltip: string;
  empty?: boolean;
  headerRight?: React.ReactNode;
  chartHeightClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[10px] border border-[#E4E9F0] bg-white p-5 shadow-sm">
      <div className="mb-3.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="font-heading text-[15px] font-bold text-blueprint-blue">{title}</h3>
            <InsightsInfoTip label={title} tooltip={tooltip} />
          </div>
          <p className="text-[11.5px] text-heritage-grey">{description}</p>
        </div>
        {headerRight}
      </div>
      {empty ? (
        <div className={cn('flex items-center justify-center text-[12px] text-heritage-grey', chartHeightClassName)}>
          No upload activity in this period.
        </div>
      ) : (
        <div className={cn('w-full', chartHeightClassName)}>{children}</div>
      )}
    </div>
  );
}

function BrandChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ color?: string; value?: number | string; name?: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg bg-blueprint-blue px-3.5 py-2.5"
      style={{ boxShadow: '0 8px 24px rgba(0,39,63,0.2)' }}
    >
      {label ? (
        <div className="mb-1 text-[10px] font-medium uppercase tracking-[0.05em] text-white/60">
          {label}
        </div>
      ) : null}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: entry.color ?? '#fff' }}
          />
          <span className="font-heading text-[12px] font-semibold text-white">
            {entry.name}: {Number(entry.value ?? 0).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

export const PipeUploadActivityInsights: React.FC<PipeUploadActivityInsightsProps> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  const kpis = data?.kpis;
  const daily = data?.daily ?? [];
  const byGroup: PipeUploadActivityPoint[] = (data?.byGroup ?? []).map((p) => ({
    ...p,
    duplicates: p.duplicates ?? 0,
    // Always show group ID on the Y-axis (never month/year labels).
    label: formatOverviewGroupLabel(String(p.key ?? '').trim() || String(p.label ?? '')),
  }));
  const hasDaily = daily.some((p) => p.uploads > 0);
  const hasByGroup = byGroup.some((p) => p.uploads > 0 || (p.duplicates ?? 0) > 0);
  const byGroupTotal = byGroup.reduce((sum, p) => sum + (p.uploads ?? 0), 0);
  const byGroupDupTotal = byGroup.reduce((sum, p) => sum + (p.duplicates ?? 0), 0);
  const longestGroupLabel = byGroup.reduce(
    (max, p) => Math.max(max, (p.label || '').length),
    8
  );
  const byGroupYAxisWidth = Math.min(180, Math.max(96, longestGroupLabel * 7));
  const byGroupChartHeight = Math.max(230, Math.min(420, 48 + byGroup.length * 36));

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <KpiSkeleton key={i} />
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 py-10 text-heritage-grey">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading insights…</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-[10px] border border-red-200 bg-red-50 p-4 text-center space-y-2">
        <p className="text-sm text-red-700">{error}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="text-[12px] text-blueprint-blue underline"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard
          label="Uploads"
          tooltip="Total pipe-end uploads in the selected period (first ingest + rescan)."
          accent="navy"
          value={(kpis?.uploadsInPeriod ?? 0).toLocaleString()}
          sub="pipe ends in this period"
        />
        <KpiCard
          label="First ingest"
          tooltip="New pipe ends first seen via createdAt (initial upload)."
          accent="blue"
          value={(kpis?.firstIngests ?? 0).toLocaleString()}
          sub="new pipe ends"
        />
        <KpiCard
          label="Rescans"
          tooltip="Pipe ends detected again after the first ingest (rescan activity)."
          accent="red"
          value={(kpis?.rescans ?? 0).toLocaleString()}
          sub="rescan events"
        />
        <KpiCard
          label="Duplicates"
          tooltip="Pipe ends that have more than one scan version for the same pipe end ID in a group (v1 + v2 + …)."
          accent="amber"
          value={(kpis?.duplicatePipeEnds ?? 0).toLocaleString()}
          sub={
            (kpis?.extraVersions ?? 0) > 0
              ? `${(kpis?.extraVersions ?? 0).toLocaleString()} extra versions beyond v1`
              : 'pipe ends with v2+'
          }
        />
        <KpiCard
          label="Active days"
          tooltip="Number of days with at least one upload in the selected period."
          accent="green"
          value={(kpis?.activeDays ?? 0).toLocaleString()}
          sub={data?.timezone ? `timezone ${data.timezone}` : 'days with uploads'}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard
          title="Daily uploads"
          description="How many pipe ends were uploaded each day"
          tooltip="Stacked bars show first ingest and rescan counts by day."
          empty={!hasDaily}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={daily} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={CHART_GRID_STROKE} vertical={false} />
              <XAxis
                dataKey="label"
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                allowDecimals={false}
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <RechartsTooltip content={<BrandChartTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
              <Bar
                dataKey="firstIngest"
                name="First ingest"
                stackId="u"
                fill={CHART_COLORS.firstIngest}
                maxBarSize={22}
              />
              <Bar
                dataKey="rescan"
                name="Rescan"
                stackId="u"
                fill={CHART_COLORS.rescan}
                radius={[3, 3, 0, 0]}
                maxBarSize={22}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Uploads by group"
          description="Upload volume by group ID (with duplicate version counts)"
          tooltip="Y-axis is group ID. Bars are first ingest + rescan. Duplicates = pipe ends in that group with more than one scan version."
          empty={!hasByGroup}
          chartHeightClassName={hasByGroup ? 'h-auto' : 'h-[230px]'}
          headerRight={
            hasByGroup ? (
              <div className="shrink-0 text-right">
                <div className="font-heading text-[13px] font-extrabold tabular-nums text-blueprint-blue">
                  {byGroupTotal.toLocaleString()}{' '}
                  <span className="text-[10px] font-bold uppercase tracking-[0.04em] text-heritage-grey">
                    uploads
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] tabular-nums text-heritage-grey">
                  {byGroupDupTotal.toLocaleString()} duplicate pipe ends
                </div>
              </div>
            ) : null
          }
        >
          <div style={{ height: byGroupChartHeight }} className="w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={byGroup}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 4, bottom: 0 }}
              >
                <CartesianGrid stroke={CHART_GRID_STROKE} horizontal={false} />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={byGroupYAxisWidth}
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <RechartsTooltip content={<BrandChartTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                />
                <Bar
                  dataKey="firstIngest"
                  name="First ingest"
                  stackId="u"
                  fill={CHART_COLORS.firstIngest}
                  maxBarSize={18}
                />
                <Bar
                  dataKey="rescan"
                  name="Rescan"
                  stackId="u"
                  fill={CHART_COLORS.rescan}
                  radius={[0, 3, 3, 0]}
                  maxBarSize={18}
                />
                <Bar
                  dataKey="duplicates"
                  name="Duplicates"
                  fill="#FFCA57"
                  maxBarSize={18}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>
    </div>
  );
};
